import React, { useState, useEffect } from "react";

export default function EditTileModal({
  isOpen,
  tileData,
  sizes = [],
  onClose,
  onSave,
  isLoading = false,
}) {
  const [formData, setFormData] = useState({
    product_size_id: "",
    application_type: "both",
    surface_type: "",
    price: "",
    sku: "",
    company_name: "",
    pieces_per_box: "",
    price_unit: "Box",
    coverage_area_per_box: "",
    coverage_area_unit: "Per Sqmr",
    weight_per_box: "",
    weight_unit: "Kg",
    thickness_mm: "",
    stock_quantity: "",
    low_stock_threshold: "",
    stock_status: "in_stock",
    images: [],
  });

  useEffect(() => {
    if (tileData) {
      setFormData({
        product_size_id: tileData.product_size_id || "",
        application_type: tileData.application_type || "both",
        surface_type: tileData.surface_type || "",
        price: tileData.price || "",
        sku: tileData.sku || "",
        company_name: tileData.company_name || "",
        pieces_per_box: tileData.pieces_per_box || "",
        price_unit: tileData.price_unit || "Box",
        coverage_area_per_box: tileData.coverage_area_per_box || "",
        coverage_area_unit: tileData.coverage_area_unit || "Per Sqmr",
        weight_per_box: tileData.weight_per_box || "",
        weight_unit: tileData.weight_unit || "Kg",
        thickness_mm: tileData.thickness_mm || "",
        stock_quantity: tileData.stock_quantity ?? "",
        low_stock_threshold: tileData.low_stock_threshold ?? "",
        stock_status: tileData.stock_status || "in_stock",
        images: [],
      });
    }
  }, [tileData]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "images") {
      setFormData((prev) => ({ ...prev, images: Array.from(files) }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const data = new FormData();

    Object.entries(formData).forEach(([key, value]) => {
      if (key === "images") {
        value.forEach((file) => data.append("images", file));
      } else if (value !== null && value !== "") {
        data.append(key, value);
      }
    });

    onSave(data);
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] flex flex-col shadow-2xl overflow-hidden font-sans border border-gray-100">
        
        {/* Modal Header */}
        <div className="px-6 py-5 flex items-center justify-between border-b border-gray-100 bg-white">
          <div>
            <h3 className="text-xl font-bold text-gray-900">Edit Tile Details</h3>
            <p className="text-xs text-gray-500 mt-0.5">Update specifications, pricing, and inventory stock.</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Scrollable Form Body */}
        <form id="edit-tile-form" onSubmit={handleSubmit} className="p-6 overflow-y-auto flex-1 space-y-6">
          
          {/* General Information */}
          <div>
            <span className="text-xs font-bold uppercase tracking-wider text-indigo-600 mb-3 block">General Info</span>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">SKU</label>
                <input
                  type="text"
                  name="sku"
                  value={formData.sku}
                  onChange={handleChange}
                  className="w-full px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg text-gray-800 focus:bg-white focus:ring-2 focus:ring-indigo-500 outline-none"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">Company / Brand</label>
                <input
                  type="text"
                  name="company_name"
                  value={formData.company_name}
                  onChange={handleChange}
                  placeholder="Manufacturer name"
                  className="w-full px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg text-gray-800 focus:bg-white focus:ring-2 focus:ring-indigo-500 outline-none"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">Product Size</label>
                <select
                  name="product_size_id"
                  value={formData.product_size_id}
                  onChange={handleChange}
                  className="w-full px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg text-gray-800 focus:bg-white focus:ring-2 focus:ring-indigo-500 outline-none"
                >
                  <option value="">Select size</option>
                  {sizes.map((size) => (
                    <option key={size.id} value={size.id}>
                      {size.name} ({size.width}×{size.height} mm)
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Specifications */}
          <div>
            <span className="text-xs font-bold uppercase tracking-wider text-indigo-600 mb-3 block">Tile Specifications</span>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">Application Type</label>
                <select
                  name="application_type"
                  value={formData.application_type}
                  onChange={handleChange}
                  className="w-full px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg text-gray-800 focus:bg-white focus:ring-2 focus:ring-indigo-500 outline-none"
                >
                  <option value="wall">Wall</option>
                  <option value="floor">Floor</option>
                  <option value="both">Both</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">Surface Type</label>
                <input
                  type="text"
                  name="surface_type"
                  value={formData.surface_type}
                  onChange={handleChange}
                  placeholder="e.g., Glossy, Matte"
                  className="w-full px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg text-gray-800 focus:bg-white focus:ring-2 focus:ring-indigo-500 outline-none"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">Thickness (mm)</label>
                <input
                  type="number"
                  step="0.1"
                  name="thickness_mm"
                  value={formData.thickness_mm}
                  onChange={handleChange}
                  className="w-full px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg text-gray-800 focus:bg-white focus:ring-2 focus:ring-indigo-500 outline-none"
                />
              </div>
            </div>
          </div>

          {/* Packaging & Pricing */}
          <div>
            <span className="text-xs font-bold uppercase tracking-wider text-indigo-600 mb-3 block">Packaging & Pricing</span>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">Price</label>
                <input
                  type="number"
                  step="0.01"
                  name="price"
                  value={formData.price}
                  onChange={handleChange}
                  className="w-full px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg text-gray-800 focus:bg-white focus:ring-2 focus:ring-indigo-500 outline-none"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">Price Unit</label>
                <select
                  name="price_unit"
                  value={formData.price_unit}
                  onChange={handleChange}
                  className="w-full px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg text-gray-800 focus:bg-white focus:ring-2 focus:ring-indigo-500 outline-none"
                >
                  <option value="Box">Box</option>
                  <option value="Sqft">Sqft</option>
                  <option value="Sqmr">Sqmr</option>
                  <option value="Piece">Piece</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">Pieces / Box</label>
                <input
                  type="number"
                  name="pieces_per_box"
                  value={formData.pieces_per_box}
                  onChange={handleChange}
                  className="w-full px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg text-gray-800 focus:bg-white focus:ring-2 focus:ring-indigo-500 outline-none"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">Coverage / Box</label>
                <input
                  type="number"
                  step="0.01"
                  name="coverage_area_per_box"
                  value={formData.coverage_area_per_box}
                  onChange={handleChange}
                  className="w-full px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg text-gray-800 focus:bg-white focus:ring-2 focus:ring-indigo-500 outline-none"
                />
              </div>
            </div>
          </div>

          {/* Stock & Inventory */}
          <div className="bg-slate-50 p-4 rounded-xl border border-slate-100">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-700 mb-3 block">Stock & Inventory</span>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">Stock Quantity</label>
                <input
                  type="number"
                  name="stock_quantity"
                  value={formData.stock_quantity}
                  onChange={handleChange}
                  placeholder="0"
                  className="w-full px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg text-gray-800 focus:ring-2 focus:ring-indigo-500 outline-none"
                />
              </div>

              {/* <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">Low Stock Threshold</label>
                <input
                  type="number"
                  name="low_stock_threshold"
                  value={formData.low_stock_threshold}
                  onChange={handleChange}
                  placeholder="e.g., 10"
                  className="w-full px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg text-gray-800 focus:ring-2 focus:ring-indigo-500 outline-none"
                />
              </div> */}

              {/* <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">Stock Status</label>
                <select
                  name="stock_status"
                  value={formData.stock_status}
                  onChange={handleChange}
                  className="w-full px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg text-gray-800 focus:ring-2 focus:ring-indigo-500 outline-none"
                >
                  <option value="in_stock">In Stock</option>
                  <option value="low_stock">Low Stock</option>
                  <option value="out_of_stock">Out of Stock</option>
                </select>
              </div> */}
            </div>
          </div>

          {/* Media */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Replace Images</label>
            <input
              type="file"
              name="images"
              onChange={handleChange}
              multiple
              accept="image/*"
              className="w-full text-xs text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100 cursor-pointer"
            />
          </div>
        </form>

        {/* Footer Controls */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2.5 text-xs font-semibold text-gray-600 bg-white border border-gray-200 rounded-xl hover:bg-gray-100 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="edit-tile-form"
            disabled={isLoading}
            className="px-6 py-2.5 text-xs font-semibold text-white bg-[#0F172A] rounded-xl hover:bg-slate-800 disabled:opacity-50 transition-colors shadow-sm"
          >
            {isLoading ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}